"use client";

import { WalletState } from "@web3-onboard/core";
import { ethers, JsonRpcProvider } from "ethers";

import { LOCALE, UNDEFINED_VALUE } from "@/constants";
import type { Address, Chain, Method } from "@/types";

const getReturnType = (method: Method): string => {
  switch (method) {
    case "canDeposit":
    case "canWithdraw":
      return "bool";
    case "getUserDepositInfo":
      return "uint256,uint256,uint256,uint256";
    default:
      return "uint256";
  }
};

export const currency = (value: number | undefined) =>
  value === undefined
    ? UNDEFINED_VALUE
    : value.toLocaleString(LOCALE, {
        currency: "USD",
        maximumFractionDigits: 2,
        style: "currency",
      });

export const fetch =
  <T = unknown>(address: Address, method: Method) =>
  ({ addresses, id, url }: Chain, ...args: string[]): Promise<T> | null =>
    addresses[address]
      ? new ethers.Contract(
          addresses[address],
          [
            `function ${method}(${args
              .map(() => "address")
              .join()}) view returns (${getReturnType(method)})`,
          ],
          new JsonRpcProvider(url, id)
        )[method](...args)
      : null;

export const getJSON =
  <T = unknown>(
    url: Parameters<typeof window.fetch>[0],
    params: Record<string, string> = {}
  ) =>
  async (): Promise<T> => {
    const urlSearchParams = new URLSearchParams(params);
    const response = await window.fetch(
      `${url}${urlSearchParams.size !== 0 ? `?${urlSearchParams}` : ""}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return await response.json();
  };

export const percentage = (value: number | undefined) =>
  value === undefined
    ? UNDEFINED_VALUE
    : `${value.toLocaleString(LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;

export const transaction =
  <T = unknown>(wallet: WalletState | null, address: Address, method: Method) =>
  async ({ addresses }: Chain, ...args: bigint[]): Promise<T | null> => {
    if (!wallet?.provider || !addresses[address]) {
      return null;
    }

    const signer = await new ethers.BrowserProvider(
      wallet.provider
    ).getSigner();

    if (method === "deposit") {
      return new ethers.Contract(
        addresses[address],
        [`function ${method}() payable`],
        signer
      )[method]({ value: args[0] });
    }

    return new ethers.Contract(
      addresses[address],
      [`function ${method}(${args.map(() => "uint256").join()})`],
      signer
    )[method](...args);
  };
